import { useState, useEffect, useCallback } from 'react';

export interface JournalResource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Encapsulates the journal pages' fetch → loading → error pattern.
 * `loader` is re-run whenever one of `deps` changes or `reload()` is called.
 * A stale-response guard ensures an older in-flight request can never overwrite
 * the result of a newer one (e.g. rapid filter/month switches).
 */
export function useJournalResource<T>(
  loader: () => Promise<T>,
  deps: readonly unknown[] = [],
  errorMessage = 'Fehler beim Laden der Daten',
): JournalResource<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadIndex, setReloadIndex] = useState(0);

  const reload = useCallback(() => setReloadIndex((i) => i + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    loader()
      .then((result) => {
        if (active) setData(result);
      })
      .catch((err) => {
        console.error(errorMessage, err);
        if (active) setError(errorMessage);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // loader/errorMessage are intentionally excluded; callers declare real deps via `deps`.
  }, [reloadIndex, ...deps]);

  return { data, loading, error, reload };
}
